import React, { Component } from "react";
import { getCurrentUser } from "aws-amplify/auth";
import "./proposalForm.css";
import {
  getInsertTypeForProgram,
  isFusionEligibleStrategy,
  isStrategyAvailableForAdvisors,
  isIspOnlyStrategy,
} from "../../utils/proposalUtils";

export class ProposalFormDetails extends Component {
  _isMounted = false;
  clientNameHistory = [];

  state = {
    username: "",
    customTemplateAvailable: false,
    logos: this.props.lists.logos,
    currentAdvisorCustom: false,
    currentVersions: {},
    versionConflict: false,
    loading: false,
  };

  componentDidMount = async () => {
    this._isMounted = true;
    try {
      const cognitoUser = await getCurrentUser();
      // In AWS Amplify v6, getCurrentUser returns the user attributes directly
      if (this._isMounted && this.props.values.userName === "") {
        const username =
          cognitoUser.username || cognitoUser.signInDetails?.loginId || "";
        this.props.values.userName = username;
        this.props.updateUserName("userName", username);
        this.setState({ username: username });
      }
      if (this._isMounted) {
        this.initializeComponentData();
      }
    } catch (e) {
      // User not authenticated - this is expected behavior
      // No need to log as error
      if (this._isMounted) {
        this.initializeComponentData();
      }
    }
  };

  // Separate initialization logic to prevent repeated calls during prop updates
  initializeComponentData = () => {
    let BreakException = {};
    try {
      this.props.lists.proposals.forEach((element) => {
        if (!this.clientNameHistory.includes(element["CLIENT NAMES"])) {
          this.clientNameHistory.push(element["CLIENT NAMES"]);
        }
      });

      // Only set default advisor if form is new and user hasn't selected anything
      if (
        this.props.values.advisorName === "" &&
        this.props.values.customTemplate === "" &&
        !this.hasUserStartedTyping() &&
        this.props.formRequestType !== "Edit" &&
        this.props.formRequestType !== "Copy"
      ) {
        this.props.handles.handleDefault(
          "advisorName",
          this.props.lists.userInfo.DefaultAdvisor,
        );
        this.props.handles.handleDefault("logoSize", "");
        this.props.handles.handleDefault("logoOffset", "");
        this.props.handles.handleDefault("program", "");
      }
    } catch (error) {
      if (error !== BreakException) throw error;
    }

    this.updateLogosFilter();
  };

  // Check if user has started typing to prevent overriding their input
  hasUserStartedTyping = () => {
    return (
      (this.props.values.clientName &&
        this.props.values.clientName.trim() !== "") ||
      (this.props.values.program && this.props.values.program.trim() !== "") ||
      (this.props.values.advisorName &&
        this.props.values.advisorName.trim() !== "")
    );
  };

  // Separate logo filter logic
  updateLogosFilter = () => {
    let logosfilter = [...this.props.lists.logos];
    logosfilter = logosfilter.filter(
      (l) =>
        String(l).split(":")[0].trim() ===
        String(
          this.props.values.advisorName
            ? this.props.values.advisorName
            : this.props.values.customTemplate,
        ).trim(),
    );
    if (
      logosfilter.length === 1 &&
      !String(this.props.values.logo || "").trim()
    ) {
      const advisorName = String(
        this.props.values.advisorName || this.props.values.customTemplate || "",
      ).trim();
      this.props.handles.handleDefault(
        "logo",
        logosfilter[0].split(":")[1].trim(),
      );
      const advisor = (this.props.lists.advisors || []).find(
        (candidate) => String(candidate.Name || "").trim() === advisorName,
      );
      if (advisor) {
        this.props.handles.handleDefault("logoSize", advisor.LogoSize);
        this.props.handles.handleDefault("logoOffset", advisor.LogoOffset);
        this.setState({
          currentAdvisorCustom:
            advisor.Custom === true || advisor.Custom === "true",
        });
      }
    }
    this.setState({ logos: logosfilter });
  };

  componentWillUnmount = () => {
    this._isMounted = false;
  };

  // Prevent re-initialization on prop updates when user is actively using the form
  componentDidUpdate = (prevProps) => {
    const advisorSelectionChanged =
      prevProps.values.advisorName !== this.props.values.advisorName ||
      prevProps.values.customTemplate !== this.props.values.customTemplate;
    const logosChanged = prevProps.lists.logos !== this.props.lists.logos;

    if (logosChanged || advisorSelectionChanged) {
      this.updateLogosFilter();
    }

    // Only update client name history if proposals list actually changed
    if (prevProps.lists.proposals !== this.props.lists.proposals) {
      this.updateClientNameHistory();
    }
  };

  // Separate method to update client name history without affecting form values
  updateClientNameHistory = () => {
    this.props.lists.proposals.forEach((element) => {
      if (!this.clientNameHistory.includes(element["CLIENT NAMES"])) {
        this.clientNameHistory.push(element["CLIENT NAMES"]);
      }
    });
  };

  continue = (e) => {
    e.preventDefault();
    this.props.nextStep();
  };

  sortAdvisors = (a, b) => {
    // Use localeCompare for proper string comparison with case-insensitivity
    return a.Name.localeCompare(b.Name, undefined, { sensitivity: "base" });
  };

  handleVersionConflictUpdate = () => {
    this.setState((prevState) => {
      return { versionConflict: !prevState.versionConflict };
    });
  };

  handleLoadingUpdate = (boolean) => {
    this.setState({ loading: boolean });
  };

  render() {
    const { currentAdvisorCustom, versionConflict, loading } = this.state;
    const { values, proposalType, handles, fusion } = this.props;
    // Use the proposal-facing list (excludes ISP-only strategies); fall back to
    // the full list if an older caller didn't provide the filtered variant.
    const {
      proposalTypesForProposals,
      proposalTypes: allProposalTypes,
      advisors,
    } = this.props.lists;
    const proposalTypes = proposalTypesForProposals || allProposalTypes || [];

    // Filter available proposal types
    const getAvailableProposalTypes = () => {
      return proposalTypes.filter((pt) => {
        // Advisor-scoped strategies: hide unless the selected advisor is on the list
        if (!isStrategyAvailableForAdvisors(pt, [values.advisorName])) {
          return false;
        }
        // ISP-only strategies are never selectable in proposal generation.
        // Primary exclusion: App.js pre-filters proposalTypesForProposals.
        // This guard is a safety net for callers that pass the raw list.
        if (isIspOnlyStrategy(pt)) {
          return false;
        }
        // Filter out fusion programs when in fusion mode
        if (fusion) {
          return isFusionEligibleStrategy(pt);
        }
        // Allow all strategies to be used regardless of client name
        return true;
      });
    };

    const availableProposalTypes = getAvailableProposalTypes();

    // Check if selected program is valid
    const isProgramValid =
      values.program !== "" &&
      availableProposalTypes.some((pt) => pt.name === values.program);

    let disableNext = true;
    if (
      isProgramValid &&
      (values.advisorName !== "" || values.customTemplate !== "") &&
      values.clientName !== "" &&
      values.insertType !== "" &&
      !loading &&
      !versionConflict
    ) {
      disableNext = false;
    }
    return (
      <div>
        <h1 style={{ display: "inline-block" }}>
          {fusion ? "Strategy Selection:" : "Proposal Strategy Selection:"}
        </h1>
        <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
          All fields are required unless marked <em>(optional)</em>.
          Fields turn <span style={{ color: "green", fontWeight: "bold" }}>green</span> when completed.
        </p>
        <div
          style={{
            display: "inline-block",
            float: "right",
            margin: "20px auto",
          }}
        >
          {/* {proposalTypes.length > 0 && this._isMounted === true && (
            <VersionUpdate
              updateLastUpdateDate={this.props.updateLastUpdateDate}
              lastUpdateDate={this.props.lastUpdateDate}
              loading={loading}
              handleLoadingUpdate={this.handleLoadingUpdate}
              versionConflict={versionConflict}
              handleVersionConflictUpdate={this.handleVersionConflictUpdate}
              versions={this.props.lists.versions}
              loggedIn={this.props.loggedIn}
            />
          )} */}
        </div>
        <div className="form-group">
          {proposalTypes.length <= 0 && (
            <div style={{ textAlign: "center" }}>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            </div>
          )}
          <label>
            <strong>Username</strong>
          </label>
          <div>{values.userName || ""}</div>
        </div>
        <div className="form-group">
          <label>
            <strong>Advisor Name(s)</strong>
          </label>
          {fusion ? (
            <div>
              {this.props.fusionStepGuideData?.advisorName ||
                values.advisorName ||
                ""}
            </div>
          ) : (
            <select
              name="advisorName"
              id="advisorName"
              list="advisorNames"
              className="form-control"
              style={{
                borderLeftColor: values.advisorName !== "" ? "green" : "#ced4da",
                borderLeftWidth: "5px",
              }}
              disabled={proposalTypes.length <= 0}
              onChange={(e) => {
                handles.handleChange("advisorName")(e);

                // Set customTemplate to the advisor name (backend will determine if they have custom templates)
                // This matches the backend logic where it checks the advisor's DynamoDB settings
                handles.handleDefault("customTemplate", e.target.value);

                // If current strategy is advisor-scoped away from the new
                // advisor, clear it so the description/validation don't cling.
                if (values.program) {
                  const pt = proposalTypes.find(
                    (p) => p.name === values.program,
                  );
                  if (
                    pt &&
                    !isStrategyAvailableForAdvisors(pt, [e.target.value])
                  ) {
                    handles.handleDefault("program", "");
                  }
                }

                let logosfilter = [...this.props.lists.logos];
                logosfilter = logosfilter.filter(
                  (l) =>
                    String(l).split(":")[0].trim() ===
                    String(e.target.value).trim(),
                );
                if (logosfilter.length > 0) {
                  handles.handleDefault(
                    "logo",
                    logosfilter[0].split(":")[1].trim(),
                  );
                  for (let advisor of advisors) {
                    if (advisor.Name === logosfilter[0].split(":")[0].trim()) {
                      this.setState({ currentAdvisorCustom: false });
                      handles.handleDefault("logoSize", advisor.LogoSize);
                      handles.handleDefault("logoOffset", advisor.LogoOffset);
                      if (advisor.Custom === "true") {
                        this.setState({ currentAdvisorCustom: true });
                      }
                      break;
                    }
                  }
                } else {
                  for (let advisor of advisors) {
                    if (advisor.Name === e.target.value) {
                      if (advisor.Custom === "true") {
                        this.setState({ currentAdvisorCustom: true });
                      }
                    }
                  }
                  handles.handleDefault("logo", "");
                  handles.handleDefault("logoSize", "");
                  handles.handleDefault("logoOffset", "");
                }
                this.setState({ logos: logosfilter });
              }}
              value={values.advisorName ? values.advisorName : ""}
            >
              {advisors
                .filter((a) => !a.Name.includes("@"))
                .sort(this.sortAdvisors)
                .map((adv, index) => (
                  <option value={adv.Name} key={index}>
                    {adv.Name}
                  </option>
                ))}
              <option value={""} key={"initialOption"}>
                Select Advisor
              </option>
            </select>
          )}
          {currentAdvisorCustom && <div>*Custom</div>}
        </div>

        {fusion && values.logo ? (
          <div className="form-group">
            <label>
              <strong>Advisor Logo</strong>
            </label>
            <div>{values.logo}</div>
          </div>
        ) : this.state.logos.length > 0 && !fusion ? (
          <div className="form-group">
            <label>
              <strong>Advisor Logo</strong> <small className="text-muted">(optional)</small>
            </label>
            <select
              name="logo"
              id="logo"
              className="form-control"
              disabled={proposalTypes.length <= 0}
              onChange={(e) => {
                handles.handleChange("logo")(e);
                for (let advisor of advisors) {
                  if (advisor.Name === e.target.value) {
                    handles.handleDefault("logoSize", advisor.LogoSize);
                    handles.handleDefault("logoOffset", advisor.LogoOffset);
                    break;
                  }
                }
              }}
              value={values.logo ? values.logo : ""}
            >
              {this.state.logos &&
                this.state.logos.map((l, index) => (
                  <option value={l.split(":")[1].trim()} key={index}>
                    {l.split(":")[1].trim()}
                  </option>
                ))}
              <option value={""} key={"initialOptionLogos"}>
                Select Logo
              </option>
            </select>
          </div>
        ) : null}
        <div className="form-group">
          <label>
            <strong>Client Name(s)</strong>
          </label>
          <input
            list="clientNames"
            name="clientName"
            id="clientName"
            disabled={proposalTypes.length <= 0}
            className="form-control"
            style={{
              borderLeftColor: values.clientName ? "green" : "#ced4da",
              borderLeftWidth: "5px",
            }}
            onChange={(e) => {
              handles.handleChange("clientName")(e);
              // No need to clear program selection anymore - strategies can be reused across clients
            }}
            defaultValue={values.clientName}
            placeholder="Enter Client Name"
          />
          <datalist id="clientNames">
            {this.clientNameHistory.map((cnh, index) => (
              <option value={cnh} key={index}>
                {cnh}
              </option>
            ))}
          </datalist>
        </div>
        <div className="form-group">
          <label>
            <strong>Strategy</strong>
          </label>
          <select
            name="program"
            id="program"
            className="form-control"
            style={{
              borderLeftColor: isProgramValid ? "green" : "#ced4da",
              borderLeftWidth: "5px",
            }}
            disabled={proposalTypes.length <= 0}
            value={values.program ? values.program : proposalType}
            onChange={(e) => {
              const programName = e.target.value;

              handles.handleChange("program")(e);
              this.props.updateProposalType(programName);

              // Set insertType based on program using shared utility
              handles.handleDefault(
                "insertType",
                getInsertTypeForProgram(programName),
              );

              // Clear income needs if not Active Income Strategy
              if (programName !== "Active Income Strategy") {
                handles.handleDefault("incomeNeeds", "");
              }

              // Clear asset value if it would be invalid for the new program
              // This provides silent validation without alerts since user is still on step 1
              if (values.assetValue && programName) {
                const currentAssetValue =
                  parseFloat(values.assetValue.replace(/[^0-9.-]+/g, "")) || 0;
                const selectedProgram = proposalTypes.find(
                  (pt) => pt.name === programName,
                );

                if (
                  selectedProgram &&
                  selectedProgram.limits &&
                  currentAssetValue > 0
                ) {
                  if (
                    currentAssetValue > Number(selectedProgram.limits.max) ||
                    currentAssetValue < Number(selectedProgram.limits.min)
                  ) {
                    // Clear the asset value silently - user will see validation on step 2
                    handles.handleDefault("assetValue", "");
                  }
                }
              }
            }}
          >
            <option value={""} key={proposalTypes.length + 1}>
              Select Strategy
            </option>
            {availableProposalTypes.map((pt, index) => (
              <option value={pt.name} key={index}>
                {pt.name}
              </option>
            ))}
          </select>
        </div>

        {/* <div className="form-group"></div>
        {!(proposalType && proposalType.toLowerCase().includes("fusion")) && (
          <React.Fragment>
            <div className="form-check form-check-inline">
              <input
                style={{ height: "15px", width: "15px" }}
                onChange={() => {
                  handles.handleDefault("insertType", "Fact Sheet");
                }}
                className="form-check-input"
                type="radio"
                name="inlineRadioOptions"
                id="inlineRadio1"
                value="Fact Sheet"
                disabled={
                  shouldDisableInsertTypeRadios(values.program) ||
                  proposalTypes.length <= 0
                }
                checked={
                  values.insertType === "Fact Sheet" || values.program === ""
                }
              />
              <label
                className="form-check-label"
                htmlFor="inlineRadio1"
              >
                Fact Sheet
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                style={{ height: "15px", width: "15px" }}
                onChange={() => handles.handleDefault("insertType", "Snapshot")}
                className="form-check-input"
                type="radio"
                name="inlineRadioOptions"
                id="inlineRadio2"
                value="Snapshot"
                disabled={
                  shouldDisableInsertTypeRadios(values.program) ||
                  proposalTypes.length <= 0
                }
                checked={values.insertType === "Snapshot"}
              />
              <label
                className="form-check-label"
                htmlFor="inlineRadio2"
              >
                Snapshot
              </label>
            </div>
          </React.Fragment>
        )} */}
        <div>
          <button
            className="btn btn-primary"
            onClick={this.continue}
            style={{
              float: "right",
              marginBottom: "10px",
              paddingLeft: "30px",
              paddingRight: "30px",
            }}
            disabled={disableNext || proposalTypes.length <= 0}
          >
            Next
          </button>
        </div>
      </div>
    );
  }
}

export default ProposalFormDetails;
